// External imports
import MuiButton, {
  ButtonProps as MuiButtonProps,
  buttonClasses,
} from '@mui/material/Button';
import React from 'react';

// Internal imports
import { CircularProgress } from './CircularProgress';

// Declare module augmentation for ButtonPropsColorOverrides
declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    alert: true;
  }
  interface ButtonPropsVariantOverrides {
    soft: true;
  }
}

export interface ButtonProps extends MuiButtonProps {
  loading?: boolean;
  color?: MuiButtonProps['color'] | 'alert';
}

export function Button<C extends React.ElementType = 'button'>(
  props: MuiButtonProps<C, { component?: C } & ButtonProps>
) {
  const {
    variant = 'text',
    color = 'primary',
    size,
    sx,
    loading,
    children,
    ...otherProps
  } = props;

  const styles = {
    transition: 'all 0.3s',
    [`.${buttonClasses.sizeSmall}`]: {
      borderRadius: (theme) => theme.tokens.base.button.radiusSmall,
      height: (theme) => theme.tokens.base.button.heightSmall,
    },
    [`.${buttonClasses.sizeMedium}`]: {
      borderRadius: (theme) => theme.tokens.base.button.radiusMedium,
      height: (theme) => theme.tokens.base.button.heightMedium,
    },
    [`.${buttonClasses.sizeLarge}`]: {
      borderRadius: (theme) => theme.tokens.base.button.radiusLarge,
      height: (theme) => theme.tokens.base.button.heightLarge,
    },
    [`&.${buttonClasses.contained}`]: {
      bgcolor: `${color}.solid`,
      color: `${color}.contrastText`,
      backgroundImage: 'none',
      boxShadow: 'none',
      border: 'none',
      '&:hover': {
        bgcolor: `${color}.solidHover`,
        outlineColor: 'transparent',
        borderColor: 'transparent',
        boxShadow: 'none',
      },
    },
    [`&.${buttonClasses.contained}.${buttonClasses.disabled}`]: {
      bgcolor: `action.disabledBackground`,
      color: `action.disabled`,
    },
    [`&.${buttonClasses.outlined}`]: {
      boxShadow: 'none',
      color: `${color}.text`,
      borderColor: `${color}.borderStrong`,
      '&:hover': {
        borderColor: `${color}.borderStrong`,
      },
    },
    [`&.${buttonClasses.outlined}.${buttonClasses.disabled}`]: {
      borderColor: `action.disabledBackground`,
      color: `action.disabled`,
    },
    [`.${buttonClasses.text}`]: {
      color: `${color}.text`,
    },
    [`.${buttonClasses.disabled}`]: {
      color: `${color}.text`,
    },
    [`&.MuiButton-soft`]: {
      bgcolor: `${color}.backgroundSoft`,
      color: `${color}.text`,
      backgroundImage: 'none',
      boxShadow: 'none',
      border: 'none',
      '&:hover': {
        bgcolor: `${color}.backgroundSoftHover`,
        outlineColor: 'transparent',
        borderColor: 'transparent',
        boxShadow: 'none',
      },
      '&:active': {
        bgcolor: `${color}.backgroundSoftActive`,
      },
    },
  };

  return (
    <MuiButton
      sx={[styles, ...(Array.isArray(sx) ? sx : [sx])]}
      variant={variant}
      color={color}
      size={size}
      {...otherProps}
    >
      {loading ? (
        <CircularProgress color="inherit" size="small" sx={{ p: '3px' }} />
      ) : (
        children
      )}
    </MuiButton>
  );
}
